import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { formatEffectiveMandateSummary } from '@/utils/mandateDisplay';
import type { DevelopmentMandateSelections } from '@/config/developmentMandate';

type MandateConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectName?: string;
  effectiveMandate: DevelopmentMandateSelections;
  onAccept: () => void;
  loading?: boolean;
};

export default function MandateConfirmDialog({
  open,
  onOpenChange,
  projectName,
  effectiveMandate,
  onAccept,
  loading = false,
}: MandateConfirmDialogProps) {
  const rows = formatEffectiveMandateSummary(effectiveMandate);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-[#2a2a2a] text-white sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-sans font-bold uppercase tracking-wide">
            Confirm effective mandate
          </DialogTitle>
          <DialogDescription className="text-white/50 font-sans text-sm mt-2">
            {projectName
              ? `Audit run for "${projectName}" will use the mandate parameters below. Scope context only — does not alter Nuclius Index scoring.`
              : 'Audit run will use the mandate parameters below. Scope context only — does not alter Nuclius Index scoring.'}
          </DialogDescription>
        </DialogHeader>
        <div className="ni-panel overflow-hidden">
          <table className="w-full text-left text-sm font-sans">
            <thead>
              <tr className="border-b border-[#2a2a2a]">
                <th className="ni-label px-4 py-2">Section</th>
                <th className="ni-label px-4 py-2">Effective values</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={2} className="px-4 py-4 text-white/45 text-sm">
                    No mandate selections configured. Account defaults and project settings are empty.
                    Update in Settings or mandate editor before proceeding.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.section} className="ni-table-row border-b border-[#2a2a2a] last:border-0">
                    <td className="px-4 py-3 text-white/55 font-mono text-xs uppercase whitespace-nowrap">
                      {row.section}
                    </td>
                    <td className="px-4 py-3 text-white/85">{row.labels}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <DialogFooter className="gap-2 mt-2">
          <Button
            variant="indexOutline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button variant="indexPrimary" onClick={onAccept} disabled={loading}>
            {loading ? 'Starting audit…' : 'Accept and run audit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
